/* eslint-disable react-refresh/only-export-components -- This module intentionally exposes its provider and typed consumer hook together. */
import GlobalSnackbar, { SnackbarMessage } from '@/components/GlobalSnackbar';
import { AlertColor, SnackbarCloseReason } from '@mui/material';
import {
  createContext,
  useState,
  useContext,
  useCallback,
  ReactNode,
  SyntheticEvent,
} from 'react';

/**
 * handleClose is shared across MUI's Snackbar.onClose, Alert.onClose, and
 * IconButton.onClick, so it must accept the union of their event types.
 */
type SnackbarCloseEvent = SyntheticEvent | Event;

interface SnackbarContextValue {
  open: boolean;
  messageInfo?: SnackbarMessage;
  severity?: AlertColor;
  showSnackbar: (message: string) => void;
  showSuccessSnackbar: (message: string) => void;
  showErrorSnackbar: (message: string) => void;
  handleClose: (
    event?: SnackbarCloseEvent,
    reason?: SnackbarCloseReason,
  ) => void;
  handleExited: () => void;
}

const SnackbarContext = createContext<SnackbarContextValue | null>(null);

interface SnackbarProviderProps {
  children: ReactNode;
  /** Passed through to GlobalSnackbar for stable Storybook snapshots. */
  settleForSnapshot?: boolean;
}

/**
 * Unique key to force React to remount the snackbar for each new message.
 * Prefer a UUID; fall back to timestamp+random where crypto.randomUUID isn't
 * available, since it only exists in a secure context.
 */
const createMessageKey = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${String(Date.now())}-${Math.random().toString(36).slice(2, 8)}`;

export const SnackbarProvider = ({
  children,
  settleForSnapshot = false,
}: SnackbarProviderProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [messageInfo, setMessageInfo] = useState<SnackbarMessage>();
  const [severity, setSeverity] = useState<AlertColor>();

  const show = (message: string, severity?: AlertColor) => {
    // Always assign, so a plain snackbar shown after a success/error one isn't
    // still rendered as an Alert carrying the previous severity.
    setSeverity(severity);

    setMessageInfo({
      message,
      key: createMessageKey(),
    });

    setOpen(true);
  };

  /**
   * Displays a black snackbar.
   */
  const showSnackbar = useCallback((message: string) => {
    show(message);
  }, []);

  /**
   * Displays a success alert wrapped in a snackbar.
   */
  const showSuccessSnackbar = useCallback((message: string) => {
    show(message, 'success');
  }, []);

  /**
   * Displays a success alert wrapped in a snackbar.
   */
  const showErrorSnackbar = useCallback((message: string) => {
    show(message, 'error');
  }, []);

  const handleClose = useCallback(
    (_event?: SnackbarCloseEvent, reason?: SnackbarCloseReason) => {
      // Don't let an unrelated click elsewhere on the page dismiss the message.
      if (reason === 'clickaway') {
        return;
      }

      setOpen(false);
    },
    [],
  );

  const handleExited = useCallback(() => {
    setMessageInfo(undefined);
  }, []);

  const contextValue = {
    open,
    messageInfo,
    showSnackbar,
    showSuccessSnackbar,
    showErrorSnackbar,
    handleClose,
    handleExited,
    severity,
  };

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <GlobalSnackbar settleForSnapshot={settleForSnapshot} />
    </SnackbarContext.Provider>
  );
};

export const useGlobalSnackbar = () => {
  const context = useContext(SnackbarContext);

  if (!context) {
    throw new Error('useGlobalSnackbar must be used within a SnackbarProvider');
  }

  return context;
};
